import { forwardRef, ReactNode } from "react";

import { LinkProps, Stack, StackProps, SxProps, Theme, useTheme } from "@mui/material";

import { FormLocationIconSquare } from "./FormLocationIconSquare";

import { FormTypography } from "@/forms-design-system/FormTypography";
import { mergeSx } from "@/forms-design-system/helpers";
import { InvalidThemeErrorMessage } from "@/forms-design-system/InvalidThemeErrorMessage";
import { useMediaQuery } from "@/hooks/useMediaQuery";

export type FormLocationProps = StackProps & {
  title: ReactNode;
  subtitle: ReactNode;
  href?: string;
  target?: LinkProps["target"];
  rel?: LinkProps["rel"];
  components?: {
    iconSquare?: {
      sx?: SxProps<Theme>;
    };
    title?: {
      sx?: SxProps<Theme>;
    };
    subtitle?: {
      sx?: SxProps<Theme>;
    };
  };
};

export const FormLocation = forwardRef<HTMLDivElement, FormLocationProps>(
  ({ title, subtitle, components, href, sx, ...rest }, ref) => {
    const theme = useTheme();
    const { isSmallScreen } = useMediaQuery();

    if (!theme.palette.forms || !theme.palette.formsComponents?.location) {
      return <InvalidThemeErrorMessage />;
    }

    const styles = mergeSx(
      {
        cursor: href ? "pointer" : undefined,
        textDecoration: "none",
        ...(href && {
          ["& h6, & p"]: {
            textDecoration: "underline",
            textDecorationColor: "transparent",
            transition: "text-decoration-color 200ms ease-in",
          },
          ["&:hover"]: {
            textDecoration: "none",
            ["& h6, & p"]: {
              textDecorationColor: theme.palette.formsComponents.location.textColor,
            },
          },
        }),
      },
      sx
    );

    return (
      <Stack
        direction="row"
        alignItems="center"
        gap={2}
        {...rest}
        ref={ref}
        component={href ? "a" : "div"}
        href={href}
        sx={styles}
      >
        <FormLocationIconSquare flexShrink={0} sx={components?.iconSquare?.sx} />
        <Stack flexGrow={1} minWidth={0}>
          <FormTypography
            color={theme.palette.formsComponents.location.textColor}
            variant={isSmallScreen ? "subtitle2" : "subtitle1"}
            sx={mergeSx({ whiteSpace: "nowrap", overflowX: "hidden", textOverflow: "ellipsis" }, components?.title?.sx)}
          >
            {title}
          </FormTypography>
          <FormTypography
            color={theme.palette.formsComponents.location.textColor}
            variant={isSmallScreen ? "body2" : "body1"}
            sx={mergeSx(
              { whiteSpace: "nowrap", overflowX: "hidden", textOverflow: "ellipsis" },
              components?.subtitle?.sx
            )}
          >
            {subtitle}
          </FormTypography>
        </Stack>
      </Stack>
    );
  }
);

FormLocation.displayName = "FormLocation";
